import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { GetAllStudentsAPI } from "@/services/api/student/getAllStudents";
import type {
  GetAllStudentsPagedResult,
  StudentVerificationFilter,
} from "@/services/api/student/getAllStudents";
import { GetAllBusinessesAPI } from "@/services/api/business/getAllBusinesses";
import type { GetAllBusinessesPagedResult } from "@/services/api/business/getAllBusinesses";
import {
  ApproveBusinessAPI,
  RejectBusinessAPI,
} from "@/services/api/business/reviews";
import {
  GetBusinessAdminAPI,
  SetBusinessActiveAPI,
  ReactivateBusinessAPI,
  CancelBusinessSubscriptionAPI,
  ResumeBusinessSubscriptionAPI,
  OpenBusinessPortalAPI,
} from "@/services/api/business/admin";
import type { BusinessAdminState } from "@/services/api/business/admin";
import { ApproveMembershipAPI } from "@/services/api/society/approveMembership";
import { universityList } from "@/assets/universityList";
import {
  classifyMembershipState,
  isMembershipActionable,
} from "@/data/studentMembershipStates";
import type { AdminMembershipState } from "@/data/studentMembershipStates";
import Icon from "@/components/Icon";
import ModalVerifyDocuments from "@/components/ModalVerifyDocuments";
import AdminListSkeleton from "@/components/AdminListSkeleton";
import AdminEmptyState from "@/components/AdminEmptyState";
import AdminStatCard from "@/components/AdminStatCard";
import AdminStatusPill from "@/components/AdminStatusPill";
import FailReasonModal from "@/components/FailReasonModal";
import Modal from "@/components/Modal";

type AccountType = "student" | "business";

/** One document the admin can review on its own; see studentVerificationDocs.js. */
type VerificationDocument = "student_card" | "hkid";

/**
 * Every impactful action in the business detail sheet runs through one shared
 * confirm dialog instead of firing straight away (or via `window.confirm`), so
 * a single misclick can never activate/deactivate, cancel, resume or reopen a
 * terminated account.
 */
type AdminConfirmAction = {
  key:
    | "activate"
    | "deactivate"
    | "reactivate"
    | "cancel_sub"
    | "resume_sub"
    | "manage_stripe"
    | "approve";
  title: string;
  message: string;
  /** What actually changes, spelled out so the admin can predict the outcome. */
  description: string;
  confirmLabel: string;
  tone: "danger" | "success" | "neutral";
};

interface SponsorEntry {
  name: string;
  image_url: string | null;
  sponsorId: string;
  activityId: string | null;
  total: number;
  completed: number;
}

const PAGE_SIZE = 20;
// Only search typing is debounced; a tab switch, page turn or filter tap is a
// deliberate action that should feel immediate.
const SEARCH_DEBOUNCE_MS = 300;

const readErrorMessage = (resp: any, fallback: string) => {
  if (typeof resp?.result === "string" && resp.result) return resp.result;
  if (typeof resp?.result?.message === "string" && resp.result.message) {
    return resp.result.message;
  }
  if (typeof resp?.resp === "string" && resp.resp) return resp.resp;
  return fallback;
};

/** Translates the stored card-invalid reason into a translation key. */
const cardInvalidReasonKey = (reason?: string | null, status?: string | null): string => {
  switch (reason) {
    case "payment_failed":
      return "Payment failed";
    case "detached":
      return "Card removed";
    case "expired":
      return "Card expired";
    case "updated":
      return "Card updated";
    default:
      if (status === "expired") return "Card expired";
      if (status === "detached") return "Card removed";
      return "Card invalid";
  }
};

/** Maps a stored subscription status to a translation key. */
const PLAN_STATUS_KEYS: Record<string, string> = {
  active: "Active",
  trialing: "Trialing",
  inactive: "Inactive",
  past_due: "Past due",
  canceled: "Cancelled",
  unpaid: "Past due",
  incomplete: "Past due",
};

const planStatusKey = (status?: string | null): string =>
  PLAN_STATUS_KEYS[status || ""] || "Inactive";

/**
 * Subscription state of a business' plan row, for the admin list card and the
 * detail modal. A terminated subscription (access locked) outranks the raw
 * status, because that lock is what actually blocks the business' features.
 */
const planSubscriptionPresentation = (
  plan?: { status?: string | null; access_status?: string | null } | null,
): {
  label: string;
  tone: "success" | "danger" | "warning" | "neutral";
  iconName: string;
} => {
  if (!plan) {
    return { label: "No plan", tone: "neutral", iconName: "minus-circle-outline" };
  }
  if (plan.access_status === "locked") {
    return { label: "Subscription terminated", tone: "danger", iconName: "cancel-circle-outline" };
  }
  switch (plan.status) {
    case "active":
      return { label: "Subscribed", tone: "success", iconName: "check-circle-outline" };
    case "trialing":
      return { label: "Trialing", tone: "success", iconName: "clock-outline" };
    case "past_due":
    case "unpaid":
    case "incomplete":
      return { label: "Past due", tone: "warning", iconName: "alert-circle-outline" };
    case "canceled":
      return { label: "Cancelled", tone: "danger", iconName: "cancel-circle-outline" };
    default:
      return { label: "No plan", tone: "neutral", iconName: "minus-circle-outline" };
  }
};

/**
 * The flags an admin scans first on a business — approval, account, subscription,
 * saved card and the first-100 promo — as one wrapping row of pills. Rendered
 * identically on the list card and in the detail modal so both present the same
 * facts in the same order. `plan` is the mirrored gate row (subscription + promo)
 * and `card` the default payment method (or null).
 */
const BusinessStatusStrip: React.FC<{
  reviewStatus: "pending" | "approved" | "rejected";
  isActive: boolean;
  plan?: any;
  card?: any;
}> = ({ reviewStatus, isActive, plan, card }) => {
  const { t } = useTranslation();
  const hasCard = Boolean(card);
  const isValid = hasCard && card.status === "valid";
  const cardLabel = !hasCard
    ? t("No card on file")
    : isValid
      ? [t("Card valid"), card.brand && card.last4 ? `${card.brand} ••••${card.last4}` : ""]
          .filter(Boolean)
          .join(" · ")
      : `${t("Card invalid")}: ${t(cardInvalidReasonKey(card.invalid_reason, card.status))}`;
  const subscription = planSubscriptionPresentation(plan);

  return (
    <div className="flex flex-wrap gap-1.5">
      <AdminStatusPill
        label={
          reviewStatus === "approved"
            ? t("Approved")
            : reviewStatus === "rejected"
              ? t("Rejected")
              : t("Awaiting admin approval")
        }
        tone={
          reviewStatus === "approved"
            ? "success"
            : reviewStatus === "rejected"
              ? "danger"
              : "warning"
        }
        iconType="MaterialCommunityIcons"
        iconName={
          reviewStatus === "approved"
            ? "check-circle"
            : reviewStatus === "rejected"
              ? "close-circle"
              : "clock-outline"
        }
        iconSize={12}
      />
      <AdminStatusPill
        label={isActive ? t("Active") : t("Inactive")}
        tone={isActive ? "success" : "neutral"}
        iconType="MaterialCommunityIcons"
        iconName={isActive ? "account-check-outline" : "account-off-outline"}
        iconSize={12}
      />
      <AdminStatusPill
        label={t(subscription.label)}
        tone={subscription.tone}
        iconType="MaterialCommunityIcons"
        iconName={subscription.iconName}
        iconSize={12}
      />
      <AdminStatusPill
        label={cardLabel}
        tone={!hasCard ? "neutral" : isValid ? "success" : "danger"}
        iconType="MaterialCommunityIcons"
        iconName={
          !hasCard
            ? "credit-card-off-outline"
            : isValid
              ? "credit-card-check-outline"
              : "credit-card-remove-outline"
        }
        iconSize={12}
      />
      {plan?.is_promo_first_100 && (
        <AdminStatusPill
          label={t("First 100 · free year")}
          tone="warning"
          iconType="MaterialCommunityIcons"
          iconName="star-circle-outline"
          iconSize={12}
        />
      )}
    </div>
  );
};

/**
 * Verification Status options, in triage order: what an admin works through
 * first (submitted work awaiting review), then the not-yet-started bulk, then
 * the approved combinations. Values mirror the server-side `VERIFICATION_STATES`
 * keys in `getAllStudents.js`.
 *
 * The three approved options are INCLUSIVE, so a student with both documents
 * approved matches "Student card verified", "HKID verified" and "Both documents
 * verified" — selecting one of them returns that student rather than nothing.
 */
const VERIFICATION_FILTER_OPTIONS: { value: StudentVerificationFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending review" },
  { value: "none", label: "No documents" },
  { value: "sc_verified", label: "Student card verified" },
  { value: "hkid_verified", label: "HKID verified" },
  { value: "both_verified", label: "Both documents verified" },
];

/**
 * Membership request state -> how it reads on screen. Only the two pending states
 * get Approve/Reject: an approved request is a membership and a rejected one is
 * the recorded audit trail of a decision already taken, so neither is work.
 */
const MEMBERSHIP_STATE_PRESENTATION: Record<
  AdminMembershipState,
  { label: string; tone: "success" | "danger" | "warning" | "neutral" }
> = {
  approved: { label: "Approved", tone: "success" },
  rejected: { label: "Rejected", tone: "danger" },
  pending_countersigned: { label: "Approved by member - awaiting admin", tone: "warning" },
  pending_unverified: { label: "Awaiting approval", tone: "neutral" },
};

/**
 * One tab's slice of server-side state. `rows` holds only the current page,
 * `total` is the size of the filtered result set behind those rows, and
 * `overallTotal` is the size of the whole tab. Keeping the two apart is what
 * lets the tab labels and stat cards report population totals (22 businesses)
 * while the list itself stays a bounded, filtered page.
 */
interface TabListState {
  rows: any[];
  total: number;
  overallTotal: number;
  /** Students only: rows whose `student_profile.verified` is not yet true. */
  pendingCount: number;
  /** Businesses only: population-wide review queue counts. */
  reviewTotals: { pending: number; approved: number; rejected: number };
  loaded: boolean;
  loading: boolean;
  error: string | null;
}

const emptyTabState = (): TabListState => ({
  rows: [],
  total: 0,
  overallTotal: 0,
  pendingCount: 0,
  reviewTotals: { pending: 0, approved: 0, rejected: 0 },
  loaded: false,
  loading: true,
  error: null,
});

/**
 * Admin user management: students + businesses tabs, search/filter,
 * expandable detail cards, document verification, and sponsor task review.
 */
const UserManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Sent explicitly with every membership decision: the wrapper otherwise falls
  // back to `student.id || admin.id`, and a browser that was last used by a
  // student would have the API reject the admin's own action.
  const admin = useSelector((state: any) => state.auth.admin);

  const [accountType, setAccountType] = useState<AccountType>("student");
  // Each tab owns its own slice of server state. The Business slice is populated
  // on mount (see loadBusinessTab) so its counts are correct before the tab is
  // ever clicked.
  const [tabLists, setTabLists] = useState<Record<AccountType, TabListState>>({
    student: emptyTabState(),
    business: emptyTabState(),
  });
  const [page, setPage] = useState(1);
  const [verifyModalVisible, setVerifyModalVisible] = useState(false);
  const [verifyUser, setVerifyUser] = useState<any | null>(null);
  // Which document the admin tapped. Carried into the modal so it shows that
  // document on its own, and cleared with the modal so the next open starts
  // blank.
  const [verifyDocument, setVerifyDocument] = useState<VerificationDocument | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);

  // Membership decision state, mirroring SocietyMembersPage: approve is confirmed
  // first, reject collects a reason (FailReasonModal) because the reason is the
  // only record of why the student was turned down.
  const [membershipAction, setMembershipAction] = useState<{
    mode: "approve" | "reject";
    membershipId: string;
  } | null>(null);
  const [membershipActionLoading, setMembershipActionLoading] = useState(false);

  const [actionLoading, setActionLoading] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<any | null>(null);
  const [selectedBusiness, setSelectedBusiness] = useState<any | null>(null);
  const [businessAdmin, setBusinessAdmin] = useState<BusinessAdminState | null>(null);
  const [businessActionLoading, setBusinessActionLoading] = useState(false);
  const [adminConfirm, setAdminConfirm] = useState<AdminConfirmAction | null>(null);

  // Filter state
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [filterVerification, setFilterVerification] = useState<StudentVerificationFilter>("all");
  const [filterActive, setFilterActive] = useState<"all" | "active" | "inactive">("all");
  const [filterReviewStatus, setFilterReviewStatus] = useState<
    "all" | "pending" | "approved" | "rejected"
  >("all");
  const [filterUniversity, setFilterUniversity] = useState<string[]>([]);

  // Sponsor selection state
  const [sponsorModalVisible, setSponsorModalVisible] = useState(false);
  const [sponsorModalEntries, setSponsorModalEntries] = useState<SponsorEntry[]>([]);
  const [sponsorModalUserId, setSponsorModalUserId] = useState<string | null>(null);

  // Monotonic per-tab request counter; see loadTab.
  const latestRequestId = useRef<Record<AccountType, number>>({ student: 0, business: 0 });

  const searchTerm = searchQuery.trim();

  const hasActiveFilters =
    accountType === "student"
      ? filterVerification !== "all" ||
        filterActive !== "all" ||
        filterUniversity.length > 0
      : filterReviewStatus !== "all" || filterActive !== "all";

  const hasQuery = searchTerm.length > 0 || hasActiveFilters;

  const active = tabLists[accountType];
  const activeTotal = active.total;
  const businessReviewTotals = tabLists.business.reviewTotals;
  const totalPages = Math.max(1, Math.ceil(activeTotal / PAGE_SIZE));

  const updateTab = useCallback(
    (type: AccountType, patch: Partial<TabListState>) =>
      setTabLists((prev) => ({ ...prev, [type]: { ...prev[type], ...patch } })),
    [],
  );

  const loadTab = useCallback(
    async (type: AccountType, targetPage: number) => {
      // Search is now a server query, so several requests can be in flight at
      // once. Only the newest one per tab is allowed to write state, otherwise a
      // slow early response could overwrite a fast later one.
      const requestId = ++latestRequestId.current[type];
      updateTab(type, { loading: true, error: null });
      const isStale = () => latestRequestId.current[type] !== requestId;
      try {
        if (type === "student") {
          const resp = await GetAllStudentsAPI({
            page: targetPage,
            size: PAGE_SIZE,
            q: searchTerm || undefined,
            verification: filterVerification,
            active: filterActive,
            university: filterUniversity,
          });
          if (isStale()) return;
          const payload = resp?.resp as GetAllStudentsPagedResult | undefined;
          if (resp?.status === 0 && payload && Array.isArray(payload.data)) {
            updateTab(type, {
              rows: payload.data,
              total: payload.total ?? payload.data.length,
              overallTotal: payload.overallTotal ?? payload.data.length,
              pendingCount: payload.pendingCount ?? 0,
              loaded: true,
              loading: false,
              error: null,
            });
          } else {
            updateTab(type, {
              loaded: true,
              loading: false,
              error: typeof resp?.resp === "string" ? resp.resp : "Failed to fetch students",
            });
          }
          return;
        }

        const resp = await GetAllBusinessesAPI({
          page: targetPage,
          size: PAGE_SIZE,
          q: searchTerm || undefined,
          active: filterActive,
          review_status: filterReviewStatus,
        });
        if (isStale()) return;
        const payload = resp?.resp as GetAllBusinessesPagedResult | undefined;
        if (resp?.status === 0 && payload && Array.isArray(payload.data)) {
          updateTab(type, {
            rows: payload.data,
            total: payload.total ?? payload.data.length,
            overallTotal: payload.overallTotal ?? payload.data.length,
            reviewTotals: payload.reviewTotals ?? { pending: 0, approved: 0, rejected: 0 },
            loaded: true,
            loading: false,
            error: null,
          });
        } else {
          updateTab(type, {
            loaded: true,
            loading: false,
            error: typeof resp?.resp === "string" ? resp.resp : "Failed to fetch businesses",
          });
        }
      } catch (err) {
        if (isStale()) return;
        console.error(`loadTab(${type}) ${err}`);
        updateTab(type, {
          loaded: true,
          loading: false,
          error: type === "student" ? "Failed to fetch students" : "Failed to fetch businesses",
        });
      }
    },
    [searchTerm, filterVerification, filterActive, filterReviewStatus, filterUniversity, updateTab],
  );

  /**
   * Unfiltered business page 1, fetched exactly once on mount. The Business tab
   * label and the `Businesses` stat card report a population total that only
   * exists in this tab's response, so waiting for the tab to be opened is what
   * left them reading zero on first paint. It deliberately reads no search or
   * filter state so it stays a single request for the life of the page.
   */
  const loadBusinessTab = useCallback(async () => {
    updateTab("business", { loading: true, error: null });
    try {
      const resp = await GetAllBusinessesAPI({ page: 1, size: PAGE_SIZE });
      const payload = resp?.resp as GetAllBusinessesPagedResult | undefined;
      if (resp?.status === 0 && payload && Array.isArray(payload.data)) {
        updateTab("business", {
          rows: payload.data,
          total: payload.total ?? payload.data.length,
          overallTotal: payload.overallTotal ?? payload.data.length,
          reviewTotals: payload.reviewTotals ?? { pending: 0, approved: 0, rejected: 0 },
          loaded: true,
          loading: false,
          error: null,
        });
      } else {
        updateTab("business", {
          loaded: true,
          loading: false,
          error: typeof resp?.resp === "string" ? resp.resp : "Failed to fetch businesses",
        });
      }
    } catch (err) {
      console.error(`loadBusinessTab ${err}`);
      updateTab("business", {
        loaded: true,
        loading: false,
        error: "Failed to fetch businesses",
      });
    }
  }, [updateTab]);

  // Only the visible tab is (re)loaded for the active page/search/filters; the
  // other tab keeps the totals it already has. Typing is debounced (a page turn
  // or a filter tap is not) so a burst of keystrokes issues one query instead of
  // one per character.
  useEffect(() => {
    const timer = setTimeout(() => {
      loadTab(accountType, page);
    }, searchTerm ? SEARCH_DEBOUNCE_MS : 0);
    return () => clearTimeout(timer);
  }, [accountType, page, searchTerm, loadTab]);

  useEffect(() => {
    loadBusinessTab();
  }, [loadBusinessTab]);

  // If the filtered result set shrank past the current page (rows removed while
  // paged, or a tab whose page-1 request is narrower than expected), step back
  // to the last page that actually has rows.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  /**
   * Page, search and filter state all feed the same request, and a page number
   * is only meaningful for the query it was requested with. Resetting it inside
   * the handlers that change the query (rather than in a follow-up effect) keeps
   * `page` and the request the effect runs with consistent, so we never fire a
   * throwaway request for a page that is about to be discarded.
   */
  const onSwitchTab = (type: AccountType) => {
    setPage(1);
    setExpandedUserId(null);
    setSearchQuery("");
    setFilterVerification("all");
    setFilterActive("all");
    setFilterReviewStatus("all");
    setFilterUniversity([]);
    setAccountType(type);
  };

  const onSearchChange = (value: string) => {
    setPage(1);
    setSearchQuery(value);
  };

  const onRefresh = useCallback(async () => {
    setPage(1);
    await loadTab(accountType, 1);
  }, [accountType, loadTab]);

  const toggleExpand = (userId: string) => {
    setExpandedUserId((prev) => (prev === userId ? null : userId));
  };

  const toggleUniversity = (uniValue: string) => {
    setPage(1);
    setFilterUniversity((prev) =>
      prev.includes(uniValue) ? prev.filter((u) => u !== uniValue) : [...prev, uniValue],
    );
  };

  const clearFilters = () => {
    setPage(1);
    setFilterVerification("all");
    setFilterActive("all");
    setFilterReviewStatus("all");
    setFilterUniversity([]);
  };

  /**
   * Open the verification dialog on ONE document. The two buttons on the row pass
   * the document they stand next to, so the admin can see what they are approving
   * before the dialog opens, the dialog shows that document alone, and the
   * approve/reject inside it targets only that document.
   */
  const openVerifyFor = (user: any, document: VerificationDocument) => {
    setVerifyUser(user);
    setVerifyDocument(document);
    setVerifyModalVisible(true);
  };

  const closeVerifyModal = () => {
    setVerifyModalVisible(false);
    setVerifyUser(null);
    setVerifyDocument(null);
  };

  /**
   * Decide a membership request, then reload the page so the badges, the pending
   * count, the stat cards and the membership states all reflect the decision.
   * `approveMembership` answers with a non-zero status rather than throwing
   * (-30075 unknown membership, -30074 not an admin), so those are surfaced
   * instead of leaving the admin thinking it worked.
   */
  const runMembershipAction = async (
    membershipId: string,
    approved: boolean,
    rejectReason?: string,
  ) => {
    setMembershipActionLoading(true);
    try {
      const resp = await ApproveMembershipAPI({
        membership_id: membershipId,
        approved,
        reject_reason: rejectReason,
        user_id: admin.id,
      });
      if (resp?.status === 0) {
        await loadTab("student", page);
        return true;
      }
      window.alert(String(resp?.message ?? resp?.error ?? t("please_try_again")));
      return false;
    } finally {
      setMembershipActionLoading(false);
    }
  };

  const confirmMembershipApprove = async () => {
    if (!membershipAction) return;
    const { membershipId } = membershipAction;
    setMembershipAction(null);
    await runMembershipAction(membershipId, true);
  };

  const submitMembershipReject = async (reason: string) => {
    if (!membershipAction) return;
    const { membershipId } = membershipAction;
    // The dialog stays open on failure so the typed reason is not thrown away.
    if (await runMembershipAction(membershipId, false, reason)) {
      setMembershipAction(null);
    }
  };

  const runBusinessDecision = async (
    user_id: string,
    decision: "approve" | "reject",
  ) => {
    setActionLoading(true);
    try {
      const resp =
        decision === "approve"
          ? await ApproveBusinessAPI(user_id)
          : await RejectBusinessAPI(user_id);
      if (resp?.status === 0) {
        setRejectTarget(null);
        closeBusiness();
        await loadTab("business", page);
        return;
      }
      window.alert(readErrorMessage(resp, t("Something went wrong")));
    } finally {
      setActionLoading(false);
    }
  };

  const loadBusinessAdmin = useCallback(async (user_id: string) => {
    const resp = await GetBusinessAdminAPI(user_id);
    if (resp?.status === 0 && resp.result) {
      setBusinessAdmin(resp.result as BusinessAdminState);
    }
  }, []);

  const openBusiness = (user: any) => {
    setSelectedBusiness(user);
    setBusinessAdmin(null);
    if (user?.id) loadBusinessAdmin(user.id);
  };

  const closeBusiness = () => {
    setSelectedBusiness(null);
    setBusinessAdmin(null);
  };

  const refreshBusinessViews = async (user_id: string) => {
    await Promise.all([loadBusinessAdmin(user_id), loadTab("business", page)]);
  };

  const toggleBusinessActive = async () => {
    if (!selectedBusiness) return;
    const current = businessAdmin ? businessAdmin.active : selectedBusiness.active !== false;
    const nextActive = !current;
    setBusinessActionLoading(true);
    try {
      const resp = await SetBusinessActiveAPI(selectedBusiness.id, nextActive);
      if (resp?.status === 0) {
        setSelectedBusiness((prev: any) =>
          prev ? { ...prev, active: resp.result?.active ?? nextActive } : prev,
        );
        await refreshBusinessViews(selectedBusiness.id);
      } else {
        window.alert(readErrorMessage(resp, t("Something went wrong")));
      }
    } finally {
      setBusinessActionLoading(false);
    }
  };

  /**
   * The cancellation copy depends on where the business sits in its 12-month
   * minimum term, so it is computed when the confirm dialog opens instead of
   * being fired straight into a blocking `window.confirm`.
   */
  const subscriptionCancelMessage = () => {
    const plan = businessAdmin?.plan;
    const termEnd = plan?.minimum_term_end ? new Date(plan.minimum_term_end) : null;
    const beforeTerm = Boolean(termEnd && termEnd.getTime() > Date.now());
    return beforeTerm
      ? t(
          "Cancelling will stop your plan at the end of your 12-month minimum term ({{date}}). Continue?",
          { date: termEnd!.toLocaleDateString() },
        )
      : t("Your plan stays active until {{date}}. Continue?", {
          date: plan?.current_period_end
            ? new Date(plan.current_period_end).toLocaleDateString()
            : t("the end of the current period"),
        });
  };

  const setBusinessSubscriptionCancel = async (cancel: boolean) => {
    if (!selectedBusiness) return;
    setBusinessActionLoading(true);
    try {
      const resp = cancel
        ? await CancelBusinessSubscriptionAPI(selectedBusiness.id)
        : await ResumeBusinessSubscriptionAPI(selectedBusiness.id);
      if (resp?.status === 0) {
        await refreshBusinessViews(selectedBusiness.id);
      } else {
        window.alert(readErrorMessage(resp, t("Something went wrong")));
      }
    } finally {
      setBusinessActionLoading(false);
    }
  };

  /**
   * Bring a terminated (canceled/locked) business back to a re-subscribable
   * state. The backend resets the plan row and re-enables the account; the
   * business can then pick a plan again.
   */
  const reactivateBusiness = async () => {
    if (!selectedBusiness) return;
    setBusinessActionLoading(true);
    try {
      const resp = await ReactivateBusinessAPI(selectedBusiness.id);
      if (resp?.status === 0) {
        if (resp.result?.reactivated) {
          window.alert(
            t("Business reactivated. The business can now choose a plan and re-subscribe."),
          );
        }
        // Keep the open modal in step with the reset plan row.
        setSelectedBusiness((prev: any) =>
          prev?.business_plan
            ? {
                ...prev,
                business_plan: {
                  ...prev.business_plan,
                  status: "inactive",
                  access_status: "active",
                  cancel_at_period_end: false,
                },
              }
            : prev,
        );
        await refreshBusinessViews(selectedBusiness.id);
      } else {
        window.alert(readErrorMessage(resp, t("Something went wrong")));
      }
    } finally {
      setBusinessActionLoading(false);
    }
  };

  const openBusinessPortal = async () => {
    if (!selectedBusiness) return;
    setBusinessActionLoading(true);
    try {
      const resp = await OpenBusinessPortalAPI(selectedBusiness.id);
      const url = resp?.result?.url;
      if (resp?.status === 0 && typeof url === "string" && url) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        window.alert(readErrorMessage(resp, t("Something went wrong")));
      }
    } finally {
      setBusinessActionLoading(false);
    }
  };

  /**
   * Single funnel for every confirm dialog in the business sheet. The dialog
   * stays up while the request runs (buttons share `businessActionLoading`) and
   * only closes afterwards, so a failure never leaves the admin staring at a
   * modal that already vanished.
   */
  const confirmAdminAction = async () => {
    if (!adminConfirm || !selectedBusiness || businessActionLoading) return;
    const { key } = adminConfirm;
    try {
      if (key === "cancel_sub" || key === "resume_sub") {
        await setBusinessSubscriptionCancel(key === "cancel_sub");
      } else if (key === "activate" || key === "deactivate") {
        await toggleBusinessActive();
      } else if (key === "reactivate") {
        await reactivateBusiness();
      } else if (key === "manage_stripe") {
        await openBusinessPortal();
      } else if (key === "approve") {
        await runBusinessDecision(selectedBusiness.id, "approve");
      }
    } finally {
      setAdminConfirm(null);
    }
  };

  const renderVerificationBadge = (verified: boolean, label: string) => (
    <span
      className="inline-flex items-center gap-1 rounded-md px-2 py-1"
      style={{ backgroundColor: verified ? "#e8f5e9" : "#fff3e0" }}
    >
      <Icon
        iconType="MaterialCommunityIcons"
        name={verified ? "check-circle" : "alert-circle"}
        size={12}
        color={verified ? "#2e7d32" : "#e65100"}
      />
      <span
        className="text-[11px] font-medium"
        style={{ color: verified ? "#2e7d32" : "#e65100" }}
      >
        {label}
      </span>
    </span>
  );

  /**
   * Per-document verify button. It sits immediately after the badge it belongs to
   * so the pairing is unambiguous, and only renders when that document is
   * actionable — uploaded and not yet verified.
   */
  const renderVerifyDocumentButton = (
    user: any,
    document: VerificationDocument,
    label: string,
  ) => (
    <button
      type="button"
      className="inline-flex items-center gap-1 rounded-md bg-[#ffa217] px-2.5 py-1 text-[11px] font-semibold text-white transition hover:opacity-90"
      onClick={(e) => {
        e.stopPropagation();
        openVerifyFor(user, document);
      }}
    >
      <Icon iconType="MaterialCommunityIcons" name="shield-check" size={13} color="#fff" />
      {label}
    </button>
  );

  /**
   * One membership request: which society, what state it is in, and — only while
   * it is pending — the admin's two decisions.
   */
  const renderMembershipRequest = (membership: any) => {
    const state = classifyMembershipState(membership);
    const presentation = MEMBERSHIP_STATE_PRESENTATION[state];
    const actionable = isMembershipActionable(membership);
    const busy =
      membershipActionLoading && membershipAction?.membershipId === membership.id;
    return (
      <div
        key={membership.id}
        className="mb-2 rounded-lg border border-gray-100 bg-[#fafafa] p-2.5"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] font-semibold text-[#333]">
              {membership.society?.name || t("Societies")}
            </div>
            <div className="mt-0.5 text-[11px] text-[#999]">
              {`${t("Requested")}: ${
                membership.requested_at
                  ? new Date(membership.requested_at).toLocaleDateString()
                  : "-"
              }`}
            </div>
          </div>
          <AdminStatusPill
            label={t(presentation.label)}
            tone={presentation.tone}
            iconType="MaterialCommunityIcons"
            iconName={
              state === "approved"
                ? "check-circle"
                : state === "rejected"
                  ? "close-circle"
                  : "clock-outline"
            }
            iconSize={12}
          />
        </div>

        {membership.rejected_reason && (
          <div className="mt-1.5 text-[11px] text-[#c62828]">
            {`${t("Why failed")}: ${membership.rejected_reason}`}
          </div>
        )}

        {actionable && (
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              className={`flex items-center gap-1 rounded-lg bg-[#4caf50] px-3.5 py-2 text-[12px] font-bold text-white transition hover:opacity-90 ${
                busy ? "opacity-50" : ""
              }`}
              disabled={membershipActionLoading}
              onClick={(e) => {
                e.stopPropagation();
                setMembershipAction({ mode: "approve", membershipId: membership.id });
              }}
            >
              <Icon iconType="MaterialCommunityIcons" name="check" size={14} color="#fff" />
              {t("Approve")}
            </button>
            <button
              type="button"
              className={`flex items-center gap-1 rounded-lg bg-[#f44336] px-3.5 py-2 text-[12px] font-bold text-white transition hover:opacity-90 ${
                busy ? "opacity-50" : ""
              }`}
              disabled={membershipActionLoading}
              onClick={(e) => {
                e.stopPropagation();
                setMembershipAction({ mode: "reject", membershipId: membership.id });
              }}
            >
              <Icon iconType="MaterialCommunityIcons" name="close" size={14} color="#fff" />
              {t("Reject")}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderFilterModal = () => (
    <Modal
      visible={filterModalVisible}
      onClose={() => setFilterModalVisible(false)}
      position="center"
    >
      <div className="rounded-[20px] bg-white shadow-lg">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div className="text-lg text-[#222]">{t("Filters")}</div>
          <button
            type="button"
            onClick={() => setFilterModalVisible(false)}
            className="text-[#333]"
          >
            <Icon iconType="Ionicons" name="close" size={24} color="#333" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-5 py-3">
          {/* Verification Status */}
          {accountType === "student" && (
            <>
              <div className="mb-3 mt-2 text-[13px] font-semibold text-[#555]">
                {t("Verification Status")}
              </div>
              <div className="mb-3 flex flex-wrap gap-2">
                {VERIFICATION_FILTER_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`flex-1 basis-[calc(50%-0.25rem)] rounded-lg border py-2.5 text-[13px] transition ${
                      filterVerification === opt.value
                        ? "border-[#ffa217] bg-[#ffa217] font-semibold text-white"
                        : "border-[#e0e0e0] bg-[#f5f5f5] text-[#666]"
                    }`}
                    onClick={() => {
                      setPage(1);
                      setFilterVerification(opt.value);
                    }}
                  >
                    {t(opt.label)}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Review Status */}
          {accountType === "business" && (
            <>
              <div className="mb-3 mt-2 text-[13px] font-semibold text-[#555]">
                {t("Review Status")}
              </div>
              <div className="mb-3 flex w-full gap-1.5">
                {(["all", "pending", "approved", "rejected"] as const).map((opt) => {
                  const selected = filterReviewStatus === opt;
                  const count =
                    opt === "pending"
                      ? businessReviewTotals.pending
                      : opt === "approved"
                        ? businessReviewTotals.approved
                        : opt === "rejected"
                          ? businessReviewTotals.rejected
                          : null;
                  return (
                    <button
                      key={opt}
                      type="button"
                      className={`flex flex-1 items-center justify-center gap-1 rounded-lg border py-2 text-[12px] transition ${
                        selected
                          ? "border-[#ffa217] bg-[#ffa217] font-semibold text-white"
                          : "border-[#e0e0e0] bg-[#f5f5f5] text-[#666]"
                      }`}
                      onClick={() => {
                        setPage(1);
                        setFilterReviewStatus(opt);
                      }}
                    >
                      {opt === "all"
                        ? t("All")
                        : opt === "pending"
                          ? t("Pending")
                          : opt === "approved"
                            ? t("Approved")
                            : t("Rejected")}
                      {count !== null && (
                        <span
                          className={`rounded-full px-1.5 py-px text-[10px] font-bold ${
                            selected ? "bg-white/25 text-white" : "bg-white text-[#999]"
                          }`}
                        >
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Active Status */}
          <div className="mb-3 mt-2 text-[13px] font-semibold text-[#555]">
            {t("Account Status")}
          </div>
          <div className="mb-3 flex gap-2">
            {(["all", "active", "inactive"] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                className={`flex-1 rounded-lg border py-2.5 text-[13px] transition ${
                  filterActive === opt
                    ? "border-[#ffa217] bg-[#ffa217] font-semibold text-white"
                    : "border-[#e0e0e0] bg-[#f5f5f5] text-[#666]"
                }`}
                onClick={() => {
                  setPage(1);
                  setFilterActive(opt);
                }}
              >
                {opt === "all" ? t("All") : opt === "active" ? t("Active") : t("Inactive")}
              </button>
            ))}
          </div>

          {/* University */}
          {accountType === "student" && (
            <>
              <div className="mb-3 mt-2 text-[13px] font-semibold text-[#555]">
                {t("University")}
              </div>
              <div className="mb-3 flex flex-wrap gap-1.5">
                {universityList.map((uni) => (
                  <button
                    key={uni.value}
                    type="button"
                    className={`rounded-full border px-3 py-1.5 text-xs transition ${
                      filterUniversity.includes(uni.value)
                        ? "border-[#ffa217] bg-[#ffa217] font-semibold text-white"
                        : "border-[#e0e0e0] bg-[#f5f5f5] text-[#666]"
                    }`}
                    onClick={() => toggleUniversity(uni.value)}
                  >
                    {t(`${uni.value}`)}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="flex gap-2.5 border-t border-gray-100 px-5 py-3.5">
          {hasActiveFilters && (
            <button
              type="button"
              className="flex-1 rounded-[10px] border border-[#e0e0e0] bg-[#f5f5f5] py-3 text-sm font-semibold text-[#666]"
              onClick={clearFilters}
            >
              {t("Clear All")}
            </button>
          )}
          <button
            type="button"
            className={`flex-1 rounded-[10px] bg-[#ffa217] py-3 text-sm text-white ${
              !hasActiveFilters ? "flex-1" : ""
            }`}
            onClick={() => setFilterModalVisible(false)}
          >
            {t("Done")}
          </button>
        </div>
      </div>
    </Modal>
  );

  const renderToolbar = (placeholder: string) => (
    <div className="mb-2 flex w-full items-center gap-2">
      <div className="flex flex-1 items-center rounded-[10px] border border-[#e0e0e0] bg-white px-3 py-2.5">
        <Icon iconType="Ionicons" name="search" size={18} color="#999" />
        <input
          className="ml-2 flex-1 text-sm text-[#333] focus:outline-none"
          placeholder={placeholder}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        {searchQuery.length > 0 && (
          <button type="button" onClick={() => onSearchChange("")}>
            <Icon iconType="Ionicons" name="close-circle" size={18} color="#999" />
          </button>
        )}
      </div>
      <button
        type="button"
        className={`flex h-[42px] w-[42px] items-center justify-center rounded-[10px] border transition ${
          hasActiveFilters
            ? "border-[#ffa217] bg-[#ffa217]"
            : "border-[#e0e0e0] bg-white"
        }`}
        onClick={() => setFilterModalVisible(true)}
      >
        <Icon
          iconType="FontAwesome"
          name="filter"
          size={18}
          color={hasActiveFilters ? "#fff" : "#ffa217"}
        />
      </button>
    </div>
  );

  /**
   * Server-side pager. `total` is the filtered result set size, so the indicator
   * and the button states describe the whole result set rather than the rows that
   * happen to be on screen.
   */
  const renderPagination = (resultTotal: number) => {
    if (resultTotal <= PAGE_SIZE) return null;
    const lastPage = Math.max(1, Math.ceil(resultTotal / PAGE_SIZE));
    const canGoBack = page > 1;
    const canGoForward = page < lastPage;
    return (
      <div className="mt-3 flex w-full items-center justify-between gap-2">
        <button
          type="button"
          disabled={!canGoBack}
          onClick={() => setPage((prev) => Math.max(1, prev - 1))}
          className={`flex items-center gap-1 rounded-[10px] border px-3 py-2 text-[13px] font-medium transition ${
            canGoBack
              ? "border-[#ffa217] bg-white text-[#ffa217]"
              : "border-[#e0e0e0] bg-[#f5f5f5] text-[#bbb]"
          }`}
        >
          <Icon iconType="MaterialCommunityIcons" name="chevron-left" size={16} color={canGoBack ? "#ffa217" : "#bbb"} />
          {t("Previous page")}
        </button>
        <div className="text-xs text-[#888]">
          {t("Page {{page}} of {{total}}", { page, total: lastPage })}
        </div>
        <button
          type="button"
          disabled={!canGoForward}
          onClick={() => setPage((prev) => Math.min(lastPage, prev + 1))}
          className={`flex items-center gap-1 rounded-[10px] border px-3 py-2 text-[13px] font-medium transition ${
            canGoForward
              ? "border-[#ffa217] bg-white text-[#ffa217]"
              : "border-[#e0e0e0] bg-[#f5f5f5] text-[#bbb]"
          }`}
        >
          {t("Next page")}
          <Icon iconType="MaterialCommunityIcons" name="chevron-right" size={16} color={canGoForward ? "#ffa217" : "#bbb"} />
        </button>
      </div>
    );
  };

  const renderStudentList = () => (
    <>
      {renderToolbar(t("Search by name, email, university..."))}

      <div className="mb-2 w-full text-xs text-[#888]">
        {`${activeTotal} student${activeTotal !== 1 ? "s" : ""}`}
      </div>

      {active.rows.length === 0 ? (
        <AdminEmptyState
          iconType="MaterialCommunityIcons"
          iconName="account-search-outline"
          title={!hasQuery && activeTotal === 0 ? t("No students found") : t("No users match your search")}
          message={
            !hasQuery && activeTotal === 0
              ? t("Pull to refresh to load students.")
              : t("Try adjusting your search or filters")
          }
          actionLabel={t("Refresh")}
          onAction={() => {
            if (!hasQuery) {
              onRefresh();
            } else {
              onSearchChange("");
              clearFilters();
            }
          }}
        />
      ) : (
        <div className="w-full">
          {active.rows.map((u) => {
            const profile = u.student_profile || {};
            const sponsorTasks = u.sponsorTaskCompletions || [];
            const totalTasks = sponsorTasks.length;
            const completedTasks = sponsorTasks.filter((task: any) => task.completed).length;
            // Group tasks by sponsor
            const sponsorMap: Record<string, SponsorEntry> = {};
            sponsorTasks.forEach((task: any) => {
              const sponsor = task.sponsor_activity?.sponsor || task.sponsor_task?.sponsor;
              const key = sponsor?.id || "unknown";
              if (!sponsorMap[key]) {
                sponsorMap[key] = {
                  name: sponsor?.name || "Unknown",
                  image_url: sponsor?.image_url || null,
                  sponsorId: sponsor?.id || "",
                  activityId: task.sponsor_activity?.id || null,
                  total: 0,
                  completed: 0,
                };
              }
              sponsorMap[key].total++;
              if (task.completed) sponsorMap[key].completed++;
            });
            const sponsorEntries = Object.values(sponsorMap);
            const expanded = expandedUserId === u.id;
            const scUploaded = !!profile.student_card_image_url;
            const hkidUploaded = !!profile.hkid_image_url;
            const scVerified = !!(profile.student_card_number && profile.student_card_name);
            const hkidVerified = !!(profile.hkid_number && profile.hkid_name);
            const scActionable = scUploaded && !scVerified;
            const hkidActionable = hkidUploaded && !hkidVerified;
            const memberships: any[] = Array.isArray(profile.memberships)
              ? profile.memberships
              : [];
            const pendingMemberships = memberships.filter(isMembershipActionable);

            let uni = null;
            if (profile.university) {
              uni = universityList.find(
                (univ) =>
                  univ.value.trim().toLowerCase() ===
                  String(profile.university).trim().toLowerCase(),
              );
            }

            return (
              <div
                key={u.id}
                className="mb-2.5 cursor-pointer rounded-xl bg-white p-3.5 shadow-sm transition hover:shadow"
                onClick={() => toggleExpand(u.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex flex-1 items-center">
                    {profile.profile_icon ? (
                      <img
                        src={profile.profile_icon}
                        alt=""
                        className="mr-2.5 h-[42px] w-[42px] rounded-full object-cover"
                      />
                    ) : (
                      <div className="mr-2.5 flex h-[42px] w-[42px] items-center justify-center rounded-full bg-[#F5F5F5]">
                        <Icon
                          iconType="MaterialCommunityIcons"
                          name="account-circle"
                          size={36}
                          color="#bbb"
                        />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[15px] font-semibold text-[#222]">
                        {u.username || u.id}
                      </div>
                      <div className="mt-0.5 flex items-center">
                        {uni && (
                          <img src={uni.image ?? undefined} alt="" className="mr-1 h-3.5 w-3.5 rounded-full" />
                        )}
                        <span className="truncate text-xs text-[#888]">
                          {uni ? t(`${uni.value}`) : profile.university || "-"}
                        </span>
                      </div>
                      {profile.university_email ? (
                        <div className="mt-0.5 truncate text-xs text-[#888]">
                          {profile.university_email}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <AdminStatusPill
                      label={u.active ? t("Active") : t("Inactive")}
                      tone={u.active ? "success" : "neutral"}
                      showDot
                      dotColor={u.active ? "#4caf50" : "#bbb"}
                    />
                  </div>
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {renderVerificationBadge(scVerified, t("Student Card"))}
                  {scActionable &&
                    renderVerifyDocumentButton(u, "student_card", t("Verify Student Card"))}
                  {renderVerificationBadge(hkidVerified, t("HKID"))}
                  {hkidActionable && renderVerifyDocumentButton(u, "hkid", t("Verify HKID"))}
                  {pendingMemberships.length > 0 && (
                    <AdminStatusPill
                      label={t("{{count}} membership requests", {
                        count: pendingMemberships.length,
                      })}
                      tone="warning"
                      iconType="MaterialCommunityIcons"
                      iconName="account-group-outline"
                      iconSize={12}
                    />
                  )}
                  {sponsorEntries.length > 0 && (
                    <span
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1"
                      style={{
                        backgroundColor: completedTasks === totalTasks ? "#e8f5e9" : "#fff3e0",
                      }}
                    >
                      <span
                        className="text-[11px] font-semibold"
                        style={{
                          color: completedTasks === totalTasks ? "#2e7d32" : "#e65100",
                        }}
                      >
                        {`${completedTasks}/${totalTasks}`}
                      </span>
                      {sponsorEntries.length > 1 && (
                        <span
                          className="text-[9px]"
                          style={{
                            color: completedTasks === totalTasks ? "#2e7d32" : "#e65100",
                          }}
                        >
                          {`· ${sponsorEntries.length} sponsors`}
                        </span>
                      )}
                    </span>
                  )}
                </div>

                {expanded && (
                  <div className="mt-2.5">
                    <div className="mb-2.5 h-px bg-gray-100" />
                    <div className="flex flex-wrap gap-2">
                      <div className="mb-1 w-[48%]">
                        <div className="text-[11px] font-medium text-[#999]">{t("Email")}</div>
                        <div className="truncate text-[13px] font-medium text-[#333]">
                          {profile.university_email || "-"}
                        </div>
                      </div>
                      <div className="mb-1 w-[48%]">
                        <div className="text-[11px] font-medium text-[#999]">{t("Phone")}</div>
                        <div className="text-[13px] font-medium text-[#333]">
                          {profile.phone_num || "-"}
                        </div>
                      </div>
                      <div className="mb-1 w-[48%]">
                        <div className="text-[11px] font-medium text-[#999]">{t("Role")}</div>
                        <div className="text-[13px] font-medium text-[#333]">{u.role || "-"}</div>
                      </div>
                      <div className="mb-1 w-[48%]">
                        <div className="text-[11px] font-medium text-[#999]">{t("Year")}</div>
                        <div className="text-[13px] font-medium text-[#333]">
                          {profile.year_of_study || "-"}
                        </div>
                      </div>
                      <div className="mb-1 w-[48%]">
                        <div className="text-[11px] font-medium text-[#999]">{t("Gender")}</div>
                        <div className="text-[13px] font-medium text-[#333]">
                          {profile.gender || "-"}
                        </div>
                      </div>
                      <div className="mb-1 w-[48%]">
                        <div className="text-[11px] font-medium text-[#999]">{t("Memberships")}</div>
                        <div className="text-[13px] font-medium text-[#333]">
                          {`${memberships.length}`}
                        </div>
                      </div>
                      {profile.ctime && (
                        <div className="mb-1 w-[48%]">
                          <div className="text-[11px] font-medium text-[#999]">{t("Joined")}</div>
                          <div className="text-[13px] font-medium text-[#333]">
                            {new Date(profile.ctime).toLocaleDateString()}
                          </div>
                        </div>
                      )}
                      {profile.identity && (
                        <div className="mb-1 w-[48%]">
                          <div className="text-[11px] font-medium text-[#999]">{t("Identity")}</div>
                          <div className="text-[13px] font-medium text-[#333]">
                            {profile.identity}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Membership requests are the admin's decisions to make, so
                        they are listed with their state here rather than reduced to
                        the count in the grid above. */}
                    {memberships.length > 0 && (
                      <div className="mt-3">
                        <div className="mb-1.5 text-[11px] font-medium text-[#999]">
                          {t("Membership requests")}
                        </div>
                        {memberships.map((membership) =>
                          renderMembershipRequest(membership),
                        )}
                      </div>
                    )}

                    {sponsorEntries.length > 0 && (
                      <button
                        type="button"
                        className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#5c6bc0] px-3.5 py-2.5 text-[13px] font-semibold text-white transition hover:opacity-90"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSponsorModalEntries(sponsorEntries);
                          setSponsorModalUserId(u.id);
                          setSponsorModalVisible(true);
                        }}
                      >
                        <Icon
                          iconType="MaterialCommunityIcons"
                          name="clipboard-check"
                          size={16}
                          color="#fff"
                        />
                        {t("Review Sponsor Tasks")}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {renderPagination(activeTotal)}
        </div>
      )}
    </>
  );

  const renderBusinessList = () => (
    <>
      {renderToolbar(t("Search by name, email, BR number..."))}

      <div className="mb-2 w-full text-xs text-[#888]">
        {`${activeTotal} business${activeTotal !== 1 ? "es" : ""}`}
      </div>

      {active.rows.length === 0 ? (
        <AdminEmptyState
          iconType="MaterialCommunityIcons"
          iconName="domain-off"
          title={!hasQuery && activeTotal === 0 ? t("No businesses found") : t("No users match your search")}
          message={
            !hasQuery && activeTotal === 0
              ? t("Pull to refresh to load businesses.")
              : t("Try adjusting your search or filters")
          }
          actionLabel={t("Refresh")}
          onAction={() => {
            if (!hasQuery) {
              onRefresh();
            } else {
              onSearchChange("");
              clearFilters();
            }
          }}
        />
      ) : (
        <div className="w-full">
          {active.rows.map((u) => {
            const profile = u.business_profile || {};
            // A row with no review_status predates the column; treat it as
            // pending so it stays reviewable, matching the pill's default.
            const reviewStatus: "pending" | "approved" | "rejected" =
              profile.review_status === "approved" || profile.review_status === "rejected"
                ? profile.review_status
                : "pending";
            const isPending = reviewStatus === "pending";
            const hasDetails = Boolean(
              profile.br_number ||
                profile.contact_number ||
                profile.company_address ||
                profile.ctime,
            );
            return (
              <button
                key={u.id}
                type="button"
                className={`mb-2.5 block w-full rounded-xl p-3.5 text-left shadow-sm transition hover:shadow ${
                  isPending ? "border-l-[3px] border-l-[#ffa217] bg-[#fffdf5]" : "bg-white"
                }`}
                onClick={() => openBusiness(u)}
              >
                {/* Zone A — identity */}
                <div className="flex items-center gap-2.5">
                  {profile.icon ? (
                    <img
                      src={profile.icon}
                      alt=""
                      className="h-[42px] w-[42px] shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full bg-[#F0F6FF]">
                      <Icon
                        iconType="MaterialCommunityIcons"
                        name="domain"
                        size={28}
                        color="#ffa217"
                      />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[15px] font-semibold text-[#222]">
                      {profile.company_name || u.username || u.id}
                    </div>
                    <div className="truncate text-xs text-[#888]">
                      {profile.company_email || "-"}
                    </div>
                  </div>
                  {profile.br_image && (
                    <span className="block shrink-0 overflow-hidden rounded-md border border-[#e0e0e0]">
                      <img
                        src={profile.br_image}
                        alt={t("Business Registration Certificate")}
                        className="h-12 w-16 object-contain"
                      />
                    </span>
                  )}
                </div>

                {/* Zone B — status flags, wrapped so they stay scannable */}
                <div className="mt-2.5">
                  <BusinessStatusStrip
                    reviewStatus={reviewStatus}
                    isActive={u.active !== false}
                    plan={u.business_plan}
                    card={u.card}
                  />
                </div>

                {/* Zone C — profile details, only when there is something to show */}
                {hasDetails && (
                  <div className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1.5 border-t border-[#f0f0f0] pt-2.5">
                    {profile.br_number && (
                      <div className="min-w-0">
                        <div className="text-[11px] text-[#999]">{t("BR Number")}</div>
                        <div className="truncate text-[13px] font-medium text-[#333]">
                          {profile.br_number}
                        </div>
                      </div>
                    )}
                    {profile.contact_number && (
                      <div className="min-w-0">
                        <div className="text-[11px] text-[#999]">{t("Contact Number")}</div>
                        <div className="truncate text-[13px] font-medium text-[#333]">
                          {profile.contact_number}
                        </div>
                      </div>
                    )}
                    {profile.company_address && (
                      <div className="col-span-2 min-w-0">
                        <div className="text-[11px] text-[#999]">{t("Company Address")}</div>
                        <div className="truncate text-[13px] font-medium text-[#333]">
                          {profile.company_address}
                        </div>
                      </div>
                    )}
                    {profile.ctime && (
                      <div className="min-w-0">
                        <div className="text-[11px] text-[#999]">{t("Joined")}</div>
                        <div className="truncate text-[13px] font-medium text-[#333]">
                          {new Date(profile.ctime).toLocaleDateString()}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {isPending && (
                  <div className="mt-2.5 flex items-center justify-end gap-1 border-t border-[#f0f0f0] pt-2.5 text-[12px] font-semibold text-[#cc7a00]">
                    {t("Review")}
                    <Icon
                      iconType="MaterialCommunityIcons"
                      name="chevron-right"
                      size={16}
                      color="#cc7a00"
                    />
                  </div>
                )}
              </button>
            );
          })}
          {renderPagination(activeTotal)}
        </div>
      )}
    </>
  );

  return (
    <div className="flex min-h-full flex-col p-4">
      <h1 className="mb-4 text-2xl font-bold text-[#222]">{t("User List")}</h1>

      <div className="mb-3 flex w-full gap-2">
        <button
          type="button"
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-[10px] border-[1.5px] py-3 transition ${
            accountType === "student"
              ? "border-[#ffa217] bg-[#ffa217]"
              : "border-[#ddd] bg-white"
          }`}
          onClick={() => onSwitchTab("student")}
        >
          <Icon
            iconType="MaterialCommunityIcons"
            name="school"
            size={18}
            color={accountType === "student" ? "#fff" : "#666"}
          />
          <span
            className={`text-sm font-medium ${
              accountType === "student" ? "font-bold text-white" : "text-[#666]"
            }`}
          >
            {`${t("Students")} (${tabLists.student.overallTotal})`}
          </span>
        </button>
        <button
          type="button"
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-[10px] border-[1.5px] py-3 transition ${
            accountType === "business"
              ? "border-[#ffa217] bg-[#ffa217]"
              : "border-[#ddd] bg-white"
          }`}
          onClick={() => onSwitchTab("business")}
        >
          <Icon
            iconType="MaterialCommunityIcons"
            name="domain"
            size={18}
            color={accountType === "business" ? "#fff" : "#666"}
          />
          <span
            className={`text-sm font-medium ${
              accountType === "business" ? "font-bold text-white" : "text-[#666]"
            }`}
          >
            {`${t("Business")} (${tabLists.business.overallTotal})`}
          </span>
          {businessReviewTotals.pending > 0 && (
            <span className="rounded-full bg-[#fff3cd] px-2 py-0.5 text-[11px] font-bold text-[#7a5c00]">
              {`${businessReviewTotals.pending} ${t("Pending")}`}
            </span>
          )}
        </button>
      </div>

      {/* Compact summary row */}
      <div className="mb-3.5 flex w-full gap-2">
        <AdminStatCard
          label={t("Total Students")}
          value={tabLists.student.overallTotal}
          iconType="MaterialCommunityIcons"
          iconName="school"
          iconColor="#2196f3"
          accentColor="#E3F2FD"
        />
        <AdminStatCard
          label={t("Pending Verification")}
          value={tabLists.student.pendingCount}
          iconType="MaterialCommunityIcons"
          iconName="shield-alert-outline"
          iconColor="#e65100"
          accentColor="#FFF3E0"
        />
        <AdminStatCard
          label={t("Businesses")}
          value={tabLists.business.overallTotal}
          iconType="MaterialCommunityIcons"
          iconName="domain"
          iconColor="#7c4dff"
          accentColor="#EDE7F6"
        />
        {accountType === "business" && (
          <AdminStatCard
            label={t("Pending Approval")}
            value={businessReviewTotals.pending}
            iconType="MaterialCommunityIcons"
            iconName="clipboard-check-outline"
            iconColor="#e65100"
            accentColor="#FFF3E0"
          />
        )}
      </div>

      {active.loading ? (
        <AdminListSkeleton count={4} />
      ) : active.error ? (
        <AdminEmptyState
          iconType="MaterialCommunityIcons"
          iconName="cloud-alert-outline"
          title={t("Something went wrong")}
          message={active.error}
          actionLabel={t("Retry")}
          onAction={() => {
            setPage(1);
            loadTab(accountType, 1);
          }}
        />
      ) : accountType === "student" ? (
        renderStudentList()
      ) : (
        renderBusinessList()
      )}

      {renderFilterModal()}

      <ModalVerifyDocuments
        visible={verifyModalVisible}
        onClose={closeVerifyModal}
        onApprove={async () => {
          closeVerifyModal();
          await loadTab("student", page);
        }}
        onReject={async () => {
          closeVerifyModal();
          await loadTab("student", page);
        }}
        studentCardImageUrl={verifyUser?.student_profile?.student_card_image_url}
        hkidImageUrl={verifyUser?.student_profile?.hkid_image_url}
        initialStudentCardNumber={verifyUser?.student_profile?.student_card_number || ""}
        initialStudentCardName={verifyUser?.student_profile?.student_card_name || ""}
        initialHKIDNumber={verifyUser?.student_profile?.hkid_number || ""}
        initialHKIDName={verifyUser?.student_profile?.hkid_name || ""}
        student_id={verifyUser?.id}
        scUploaded={!!verifyUser?.student_profile?.student_card_image_url}
        hkidUploaded={!!verifyUser?.student_profile?.hkid_image_url}
        scVerified={!!(verifyUser?.student_profile?.student_card_number && verifyUser?.student_profile?.student_card_name)}
        hkidVerified={!!(verifyUser?.student_profile?.hkid_number && verifyUser?.student_profile?.hkid_name)}
        document={verifyDocument}
      />

      {/* Membership approve confirm — same pattern as SocietyMembersPage */}
      <Modal
        visible={membershipAction?.mode === "approve"}
        onClose={() => setMembershipAction(null)}
      >
        <div className="w-[85%] max-w-[360px] rounded-2xl bg-white p-5 text-center shadow-lg">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#e8f5e9]">
            <Icon iconType="MaterialCommunityIcons" name="check" size={28} color="#4caf50" />
          </div>
          <div className="mb-1 text-base font-semibold text-[#222]">{t("Approve")}</div>
          <div className="mb-3.5 text-[13px] text-[#888]">
            {t("Approve this membership request?")}
          </div>
          <div className="flex w-full gap-3">
            <button
              type="button"
              className="flex-1 rounded-[10px] bg-[#f5f5f5] py-3 text-sm font-semibold text-[#666]"
              onClick={() => setMembershipAction(null)}
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              className="flex-1 rounded-[10px] bg-[#4caf50] py-3 text-sm font-semibold text-white"
              onClick={confirmMembershipApprove}
              disabled={membershipActionLoading}
            >
              {membershipActionLoading ? "..." : t("Approve")}
            </button>
          </div>
        </div>
      </Modal>

      <FailReasonModal
        visible={membershipAction?.mode === "reject"}
        onClose={() => setMembershipAction(null)}
        loading={membershipActionLoading}
        title={t("Reject Membership Request")}
        onSubmit={submitMembershipReject}
      />

      <Modal visible={!!rejectTarget} onClose={() => setRejectTarget(null)}>
        <div className="mx-4 w-full max-w-[360px] rounded-2xl bg-white p-5 text-center shadow-lg">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#ffebee]">
            <Icon iconType="MaterialCommunityIcons" name="close" size={28} color="#f44336" />
          </div>
          <div className="mb-1 text-base font-semibold text-[#222]">
            {t("Reject this business?")}
          </div>
          <div className="mb-3.5 text-[13px] text-[#888]">
            {rejectTarget?.business_profile?.company_name || rejectTarget?.username || ""}
          </div>
          <div className="mb-3.5 text-[12px] text-[#c62828]">
            {t("This action cannot be undone")}
          </div>
          <div className="flex w-full gap-3">
            <button
              type="button"
              className="flex-1 rounded-[10px] bg-[#f5f5f5] py-3 text-sm font-semibold text-[#666]"
              onClick={() => setRejectTarget(null)}
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              className="flex-1 rounded-[10px] bg-[#f44336] py-3 text-sm font-semibold text-white"
              disabled={actionLoading}
              onClick={() => rejectTarget && runBusinessDecision(rejectTarget.id, "reject")}
            >
              {actionLoading ? "..." : t("Reject")}
            </button>
          </div>
        </div>
      </Modal>

      <Modal visible={!!selectedBusiness} onClose={closeBusiness}>
        {selectedBusiness &&
          (() => {
            const profile = selectedBusiness.business_profile || {};
            const reviewStatus: "pending" | "approved" | "rejected" =
              profile.review_status === "approved" || profile.review_status === "rejected"
                ? profile.review_status
                : "pending";
            const isPending = reviewStatus === "pending";
            const adminPlan = businessAdmin?.plan || null;
            const adminCancelScheduled = Boolean(
              adminPlan?.cancel_at || adminPlan?.cancel_at_period_end,
            );
            // Terminated = the subscription ended for good, so the only useful
            // action is reactivating; cancel/resume no longer mean anything.
            const isTerminated =
              adminPlan?.status === "canceled" || adminPlan?.access_status === "locked";
            const isBusinessActive = businessAdmin
              ? businessAdmin.active
              : selectedBusiness.active !== false;
            return (
              <div className="max-h-[90vh] w-full overflow-y-auto rounded-2xl bg-white p-5 shadow-lg">
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div className="flex min-w-0 flex-1 items-center">
                    {profile.icon ? (
                      <img
                        src={profile.icon}
                        alt=""
                        className="mr-2.5 h-[42px] w-[42px] rounded-full object-cover"
                      />
                    ) : (
                      <div className="mr-2.5 flex h-[42px] w-[42px] items-center justify-center rounded-full bg-[#F0F6FF]">
                        <Icon
                          iconType="MaterialCommunityIcons"
                          name="domain"
                          size={28}
                          color="#ffa217"
                        />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[15px] font-semibold text-[#222]">
                        {profile.company_name || selectedBusiness.username || selectedBusiness.id}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-[#888]">
                        {profile.company_email || "-"}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="shrink-0"
                    onClick={closeBusiness}
                  >
                    <Icon iconType="Ionicons" name="close" size={22} color="#333" />
                  </button>
                </div>

                {/* Status strip — same facts, same order as the list card */}
                <div className="mb-4">
                  <BusinessStatusStrip
                    reviewStatus={reviewStatus}
                    isActive={isBusinessActive}
                    plan={selectedBusiness.business_plan}
                    card={selectedBusiness.card}
                  />
                </div>

                {/* Profile details */}
                <div className="mb-4">
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#999]">
                    {t("Profile details")}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-1.5">
                      <Icon
                        iconType="MaterialCommunityIcons"
                        name="office-building"
                        size={14}
                        color="#888"
                      />
                      <span className="text-[11px] font-medium text-[#999]">{t("BR Number")}</span>
                      <span className="truncate text-[13px] font-medium text-[#333]">
                        {profile.br_number || "-"}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Icon iconType="Ionicons" name="call-outline" size={14} color="#888" />
                      <span className="text-[11px] font-medium text-[#999]">{t("Contact Number")}</span>
                      <span className="truncate text-[13px] font-medium text-[#333]">
                        {profile.contact_number || "-"}
                      </span>
                    </div>
                    <div className="flex items-start gap-1.5">
                      <Icon iconType="Ionicons" name="location-outline" size={14} color="#888" />
                      <span className="text-[11px] font-medium text-[#999]">{t("Company Address")}</span>
                      <span className="flex-1 text-[13px] font-medium text-[#333]">
                        {profile.company_address || "-"}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Icon iconType="Ionicons" name="calendar-outline" size={14} color="#888" />
                      <span className="text-[11px] font-medium text-[#999]">{t("Joined")}</span>
                      <span className="text-[13px] font-medium text-[#333]">
                        {profile.ctime ? new Date(profile.ctime).toLocaleDateString() : "-"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Promo strip — the first-100 flag and trial end, kept apart
                    from the account panel so it never reads as a status */}
                {(selectedBusiness.business_plan?.is_promo_first_100 ||
                  (adminPlan?.status === "active" && adminPlan?.free_trial_end)) && (
                  <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-[#ffe0b2] bg-[#fff8e1] px-3 py-2">
                    {selectedBusiness.business_plan?.is_promo_first_100 && (
                      <AdminStatusPill
                        label={t("First 100 · free year")}
                        tone="warning"
                        iconType="MaterialCommunityIcons"
                        iconName="star-circle-outline"
                        iconSize={12}
                      />
                    )}
                    {adminPlan?.status === "active" && adminPlan?.free_trial_end && (
                      <span className="text-[12px] font-medium text-[#8a5a00]">
                        {`${t("Free trial ends")} ${new Date(
                          adminPlan.free_trial_end,
                        ).toLocaleDateString()}`}
                      </span>
                    )}
                  </div>
                )}

                {/* ACCOUNT — the sign-in flag only; billing lives below */}
                <div className="mb-4 rounded-xl border border-[#eee] bg-[#fafafa] p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-[#999]">
                      {t("Account status")}
                    </span>
                    <AdminStatusPill
                      label={isBusinessActive ? t("Active") : t("Inactive")}
                      tone={isBusinessActive ? "success" : "neutral"}
                      iconType="MaterialCommunityIcons"
                      iconName={isBusinessActive ? "account-check-outline" : "account-off-outline"}
                      iconSize={12}
                    />
                  </div>
                  <button
                    type="button"
                    className={`flex w-full items-center justify-center gap-1 rounded-[10px] border bg-white py-2.5 text-[13px] font-semibold transition disabled:opacity-50 ${
                      isBusinessActive
                        ? "border-[#f44336] text-[#f44336]"
                        : "border-[#4caf50] text-[#2e7d32]"
                    }`}
                    disabled={businessActionLoading}
                    onClick={() =>
                      setAdminConfirm(
                        isBusinessActive
                          ? {
                              key: "deactivate",
                              title: t("Deactivate this business?"),
                              message: t(
                                "The business will be logged out and cannot sign in until it is activated again.",
                              ),
                              description: t(
                                "Only the login flag changes. The subscription keeps running in Stripe, so renewals, platform fees and late fees are unaffected.",
                              ),
                              confirmLabel: t("Deactivate"),
                              tone: "danger",
                            }
                          : {
                              key: "activate",
                              title: t("Activate this business?"),
                              message: t(
                                "The business will be able to sign in and use the platform again.",
                              ),
                              description: t(
                                "Only the login flag changes. Subscription status and access stay exactly as they are.",
                              ),
                              confirmLabel: t("Activate"),
                              tone: "success",
                            },
                      )
                    }
                  >
                    <Icon
                      iconType="MaterialCommunityIcons"
                      name={isBusinessActive ? "account-off-outline" : "account-check-outline"}
                      size={15}
                      color={isBusinessActive ? "#f44336" : "#2e7d32"}
                    />
                    {isBusinessActive ? t("Deactivate business") : t("Activate business")}
                  </button>
                  <p className="mt-2 text-[11px] leading-[1.5] text-[#999]">
                    {t(
                      "The account flag only controls sign-in. Billing is managed in the Subscription section below.",
                    )}
                  </p>
                </div>

                {/* SUBSCRIPTION — read-only facts first, then the actions */}
                <div className="mb-4 rounded-xl border border-[#eee] bg-[#fafafa] p-3">
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#999]">
                    {t("Subscription")}
                  </div>
                  {adminPlan ? (
                    <>
                      <div className="mb-2.5 flex flex-col gap-1 text-[12px] text-[#666]">
                        <div className="flex items-center justify-between gap-2">
                          <span>{t("Plan")}</span>
                          <span className="font-medium text-[#333]">
                            {adminPlan.plan_type === "yearly" ? t("Yearly") : t("Monthly")}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span>{t("Status")}</span>
                          <span className="font-medium text-[#333]">
                            {t(planStatusKey(adminPlan.status))}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span>
                            {adminCancelScheduled ? t("Ends on") : t("Renews on")}
                          </span>
                          <span className="font-medium text-[#333]">
                            {adminPlan.current_period_end
                              ? new Date(adminPlan.current_period_end).toLocaleDateString()
                              : "-"}
                          </span>
                        </div>
                        {adminPlan.minimum_term_end && (
                          <div className="flex items-center justify-between gap-2">
                            <span>{t("Minimum term")}</span>
                            <span className="font-medium text-[#333]">
                              {new Date(adminPlan.minimum_term_end).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                        {adminPlan.cancel_at && (
                          <div className="flex items-center justify-between gap-2">
                            <span>{t("Cancel at")}</span>
                            <span className="font-medium text-[#333]">
                              {new Date(adminPlan.cancel_at).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center justify-between gap-2">
                          <span>{t("Access")}</span>
                          <AdminStatusPill
                            label={
                              adminPlan.access_status === "locked"
                                ? t("Terminated instantly")
                                : t("Active")
                            }
                            tone={adminPlan.access_status === "locked" ? "danger" : "success"}
                            iconType="MaterialCommunityIcons"
                            iconName={
                              adminPlan.access_status === "locked"
                                ? "lock-check-outline"
                                : "lock-open-outline"
                            }
                            iconSize={12}
                          />
                        </div>
                        {adminPlan.access_status === "locked" && (
                          <div className="mt-1 rounded-[10px] border border-[#ffcdd2] bg-[#ffebee] px-2.5 py-2 text-[11px] leading-[1.5] text-[#c62828]">
                            {t(
                              "Terminated instantly: the subscription ended, so all paid features are blocked. Use Reactivate to let the business choose a plan again.",
                            )}
                          </div>
                        )}
                        {adminPlan.status === "active" && adminPlan.free_trial_end && (
                          <div className="flex items-center justify-between gap-2">
                            <span>{t("Free trial ends")}</span>
                            <span className="font-medium text-[#333]">
                              {new Date(adminPlan.free_trial_end).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {isTerminated ? (
                          <button
                            type="button"
                            className="flex flex-1 items-center justify-center gap-1 rounded-[10px] border border-[#4caf50] bg-white px-3 py-2 text-[12px] font-semibold text-[#2e7d32] transition disabled:opacity-50"
                            disabled={businessActionLoading}
                            onClick={() =>
                              setAdminConfirm({
                                key: "reactivate",
                                title: t("Reactivate this business?"),
                                message: t(
                                  "The subscription is reset so the business can choose a plan and re-subscribe. The saved card and promo stay on file.",
                                ),
                                description: t(
                                  "The ended subscription is dropped, access unlocks right away and status becomes No plan. The original 12-month minimum term still applies.",
                                ),
                                confirmLabel: t("Reactivate"),
                                tone: "success",
                              })
                            }
                          >
                            <Icon
                              iconType="MaterialCommunityIcons"
                              name="autorenew"
                              size={15}
                              color="#2e7d32"
                            />
                            {t("Reactivate")}
                          </button>
                        ) : adminPlan.has_subscription ? (
                          adminCancelScheduled ? (
                            <button
                              type="button"
                              className="flex flex-1 items-center justify-center gap-1 rounded-[10px] border border-[#4caf50] bg-white px-3 py-2 text-[12px] font-semibold text-[#2e7d32] transition disabled:opacity-50"
                              disabled={businessActionLoading}
                              onClick={() =>
                                setAdminConfirm({
                                  key: "resume_sub",
                                  title: t("Resume this subscription?"),
                                  message: t(
                                    "Auto-renewal is re-enabled and the business keeps its current period.",
                                  ),
                                  description: t(
                                    "Only the scheduled cancellation is cleared. Nothing is charged now and the price does not change.",
                                  ),
                                  confirmLabel: t("Resume subscription"),
                                  tone: "success",
                                })
                              }
                            >
                              <Icon
                                iconType="MaterialCommunityIcons"
                                name="play-circle-outline"
                                size={15}
                                color="#2e7d32"
                              />
                              {t("Resume subscription")}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="flex flex-1 items-center justify-center gap-1 rounded-[10px] border border-[#f44336] bg-white px-3 py-2 text-[12px] font-semibold text-[#f44336] transition disabled:opacity-50"
                              disabled={businessActionLoading}
                              onClick={() =>
                                setAdminConfirm({
                                  key: "cancel_sub",
                                  title: t("Cancel this subscription?"),
                                  message: subscriptionCancelMessage(),
                                  description: t(
                                    "This schedules the cancellation, it does not end the subscription now. The business keeps every paid feature and is still billed until the date above; only afterwards does Stripe end it and terminate access.",
                                  ),
                                  confirmLabel: t("Cancel subscription"),
                                  tone: "danger",
                                })
                              }
                            >
                              <Icon
                                iconType="MaterialCommunityIcons"
                                name="cancel"
                                size={15}
                                color="#f44336"
                              />
                              {t("Cancel subscription")}
                            </button>
                          )
                        ) : null}
                        <button
                          type="button"
                          className="flex flex-1 items-center justify-center gap-1 rounded-[10px] border border-[#ddd] bg-white px-3 py-2 text-[12px] font-semibold text-[#444] transition disabled:opacity-50"
                          disabled={businessActionLoading || !adminPlan.has_customer}
                          onClick={() =>
                            setAdminConfirm({
                              key: "manage_stripe",
                              title: t("Manage in Stripe?"),
                              message: t(
                                "Open the Stripe Billing Portal in a new tab to update the card or manage the subscription.",
                              ),
                              description: t(
                                "This opens Stripe in another tab. Nothing in Uninfo changes until an update is made inside Stripe.",
                              ),
                              confirmLabel: t("Open"),
                              tone: "neutral",
                            })
                          }
                        >
                          <Icon
                            iconType="MaterialCommunityIcons"
                            name="open-in-new"
                            size={15}
                            color="#444"
                          />
                          {t("Manage in Stripe")}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="text-[12px] text-[#999]">{t("No subscription")}</div>
                  )}
                </div>

                <div className="mb-4">
                  <div className="mb-1.5 text-[11px] font-medium text-[#999]">
                    {t("Business Registration Certificate")}
                  </div>
                  {profile.br_image ? (
                    <div className="rounded-xl border border-[#e0e0e0] p-2">
                      <img
                        src={profile.br_image}
                        alt={t("Business Registration Certificate")}
                        className="max-h-[60vh] w-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-[#e0e0e0] bg-[#fafafa] px-3 py-6 text-center text-xs text-[#999]">
                      {t("Not uploaded")}
                    </div>
                  )}
                </div>

                <div className="flex gap-2.5 border-t border-[#f0f0f0] pt-3.5">
                  {isPending ? (
                    <>
                      <button
                        type="button"
                        className={`flex flex-1 items-center justify-center gap-1 rounded-[10px] bg-[#4caf50] py-3 text-sm font-bold text-white transition hover:opacity-90 ${
                          actionLoading ? "opacity-50" : ""
                        }`}
                        disabled={actionLoading}
                        onClick={() =>
                          setAdminConfirm({
                            key: "approve",
                            title: t("Approve this business?"),
                            message:
                              profile.company_name || selectedBusiness.username || "",
                            description: t(
                              "The business is allowed to publish coupons, sponsors and activities. Billing still depends on having an active subscription.",
                            ),
                            confirmLabel: t("Approve"),
                            tone: "success",
                          })
                        }
                      >
                        <Icon
                          iconType="MaterialCommunityIcons"
                          name="check"
                          size={16}
                          color="#fff"
                        />
                        {t("Approve")}
                      </button>
                      <button
                        type="button"
                        className={`flex flex-1 items-center justify-center gap-1 rounded-[10px] border border-[#f44336] bg-white py-3 text-sm font-bold text-[#f44336] transition hover:opacity-90 ${
                          actionLoading ? "opacity-50" : ""
                        }`}
                        disabled={actionLoading}
                        onClick={() => {
                          setRejectTarget(selectedBusiness);
                          closeBusiness();
                        }}
                      >
                        <Icon
                          iconType="MaterialCommunityIcons"
                          name="close"
                          size={16}
                          color="#f44336"
                        />
                        {t("Reject")}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="flex-1 rounded-[10px] bg-[#f5f5f5] py-3 text-sm font-semibold text-[#666]"
                      onClick={closeBusiness}
                    >
                      {t("Close")}
                    </button>
                  )}
                </div>
              </div>
            );
          })()}
      </Modal>

      {/* Shared confirm dialog for every impactful business action — see
          AdminConfirmAction. It is rendered after the detail sheet so it stacks
          on top of it instead of behind. */}
      <Modal
        visible={!!adminConfirm}
        onClose={() => {
          if (!businessActionLoading && !actionLoading) setAdminConfirm(null);
        }}
      >
        <div className="mx-4 w-full max-w-[360px] rounded-2xl bg-white p-5 text-center shadow-lg">
          <div
            className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full"
            style={{
              backgroundColor:
                adminConfirm?.tone === "danger"
                  ? "#ffebee"
                  : adminConfirm?.tone === "success"
                    ? "#e8f5e9"
                    : "#fff3e0",
            }}
          >
            <Icon
              iconType="MaterialCommunityIcons"
              name={
                adminConfirm?.tone === "danger"
                  ? "alert"
                  : adminConfirm?.tone === "success"
                    ? "check-circle"
                    : "information"
              }
              size={28}
              color={
                adminConfirm?.tone === "danger"
                  ? "#f44336"
                  : adminConfirm?.tone === "success"
                    ? "#4caf50"
                    : "#ffa217"
              }
            />
          </div>
          <div className="mb-1 text-base font-semibold text-[#222]">
            {adminConfirm?.title}
          </div>
          <div className="mb-2 text-[13px] text-[#666]">{adminConfirm?.message}</div>
          <div className="mb-4 rounded-[10px] bg-[#f7f7f7] px-3 py-2 text-left text-[12px] leading-[1.55] text-[#777]">
            {adminConfirm?.description}
          </div>
          <div className="flex w-full gap-3">
            <button
              type="button"
              className="flex-1 rounded-[10px] bg-[#f5f5f5] py-3 text-sm font-semibold text-[#666] disabled:opacity-50"
              onClick={() => setAdminConfirm(null)}
              disabled={businessActionLoading || actionLoading}
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              className="flex-1 rounded-[10px] py-3 text-sm font-semibold text-white disabled:opacity-50"
              style={{
                backgroundColor:
                  adminConfirm?.tone === "danger"
                    ? "#f44336"
                    : adminConfirm?.tone === "success"
                      ? "#4caf50"
                      : "#ffa217",
              }}
              onClick={confirmAdminAction}
              disabled={businessActionLoading || actionLoading}
            >
              {businessActionLoading || actionLoading ? "..." : adminConfirm?.confirmLabel}
            </button>
          </div>
        </div>
      </Modal>

      {/* Sponsor Selection Modal */}
      <Modal
        visible={sponsorModalVisible}
        onClose={() => setSponsorModalVisible(false)}
      >
        <div className="rounded-2xl bg-white p-5 shadow-lg">
          <div className="text-base font-semibold text-[#333]">{t("Select Sponsor")}</div>
          <div className="mb-3.5 text-xs text-[#888]">
            {t("Select a sponsor redemption to view.")}
          </div>
          <div className="max-h-[300px] overflow-y-auto">
            {sponsorModalEntries.map((s, idx) => (
              <button
                key={idx}
                type="button"
                className="flex w-full items-center gap-2.5 border-b border-gray-100 py-2.5 text-left transition hover:bg-gray-50"
                onClick={() => {
                  setSponsorModalVisible(false);
                  if (s.activityId) {
                    navigate(`/console/sponsors/${s.activityId}`);
                  } else {
                    navigate(
                      `/console/sponsors/user/${sponsorModalUserId}?sponsorId=${s.sponsorId}`,
                    );
                  }
                }}
              >
                {s.image_url ? (
                  <img
                    src={s.image_url}
                    alt=""
                    className="h-9 w-9 rounded-md object-cover"
                  />
                ) : (
                  <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#e0e0e0]">
                    <Icon iconType="MaterialCommunityIcons" name="domain" size={16} color="#999" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-[#333]">{s.name}</div>
                  <div
                    className="text-xs font-medium"
                    style={{ color: s.completed === s.total ? "#2e7d32" : "#e65100" }}
                  >
                    {`${s.completed}/${s.total} tasks`}
                  </div>
                </div>
                <Icon iconType="MaterialCommunityIcons" name="chevron-right" size={20} color="#ccc" />
              </button>
            ))}
          </div>
          <button
            type="button"
            className="mt-3.5 w-full py-2.5 text-center text-sm font-medium text-[#888]"
            onClick={() => setSponsorModalVisible(false)}
          >
            {t("Cancel")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default UserManagementPage;